// CR122: Phase 4 Publish Steps UI Component
// CR131.2: Two-step promotion flow (S7A: Promote to Admin, S7B: Publish to Live)
// CR283: Added Snapshot Deal Decisions panel
// CR319: Added S6.5 Detect Reward Brand Merchants step
// CR331: Added S6.5 reward merchants diagnostics table
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Eye, Upload, Trash2, ChevronDown, Loader2, CheckCircle2,
  XCircle, Clock, AlertCircle, ExternalLink,
  ArrowRight, Globe, Settings2, Users, Search as SearchIcon
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSiteState, type SiteState, type PromoteResult, type PublishLiveResult } from "@/hooks/useSiteState";
import { SnapshotDealDecisionsPanel } from "./SnapshotDealDecisionsPanel";
import { S65RewardMerchantsTable } from "./S65RewardMerchantsTable";

interface BatchRunStep {
  id: string;
  step_key: string;
  step_name: string;
  status: string;
  records_in: number;
  records_out: number;
  finished_at: string | null;
  error_message: string | null;
  skipped_reason: string | null;
  config_json: Record<string, unknown>;
}

interface Phase4PublishStepsProps {
  batchRunId: string;
  steps: BatchRunStep[];
  onStepExecuted: () => void;
  onNavigateToDeduped: () => void;
  /** CR434: Whether this is a flyer-sourced run */
  sourcesEnabled?: string[];
  /** CR434: Callback to trigger a step run */
  onRunStep?: (stepKey: string) => void;
}

interface CleanupConfig {
  s8_1_storageEnabled: boolean;
  s8_2_dbHeavyColumnsEnabled: boolean;
  s8_3_stagingPruneEnabled: boolean;
  s8_mode: "KEEP_CONFIRMED_ONLY" | "KEEP_ALL" | "PURGE_ALL";
  s8_cleanup_preserve_candidate_assets_enabled: boolean;
  s8_capture_publish_snapshot_enabled: boolean;
}

const DEFAULT_CLEANUP_CONFIG: CleanupConfig = {
  s8_1_storageEnabled: true,
  s8_2_dbHeavyColumnsEnabled: true,
  s8_3_stagingPruneEnabled: false,
  s8_mode: "KEEP_CONFIRMED_ONLY",
  s8_cleanup_preserve_candidate_assets_enabled: true,
  s8_capture_publish_snapshot_enabled: true,
};

interface CleanupPreviewSummary {
  mode: string;
  preserve: {
    manifest_rows: number;
    candidate_pages: number;
    snippets: number;
  };
  storage: {
    considered: number;
    to_delete: number;
    skipped_preserve: number;
  };
  bucket_stats: Array<{
    bucket: string;
    planned: number;
    deleted: number;
    failed: number;
  }>;
  warnings?: string[];
  errors?: string[];
  sample_delete_paths?: Array<{
    page_truth_id: string;
    bucket: string;
    path: string;
    page_number: number | null;
    merchant_source_id: string | null;
  }>;
}

// CR557: Publish steps use left icon as primary status indicator; right-side badges suppressed for calm UI.
const stepStatusConfig: Record<string, { label: string; icon: React.ReactNode; variant: "default" | "secondary" | "destructive" | "outline"; showBadge: boolean }> = {
  NOT_STARTED: { label: "Not Started", icon: <Clock className="h-4 w-4" />, variant: "outline", showBadge: false },
  WAITING: { label: "Waiting", icon: <Clock className="h-4 w-4" />, variant: "outline", showBadge: false },
  READY: { label: "Ready", icon: <Clock className="h-4 w-4" />, variant: "outline", showBadge: false },
  RUNNING: { label: "Running", icon: <Loader2 className="h-4 w-4 animate-spin" />, variant: "secondary", showBadge: true },
  COMPLETE: { label: "Complete", icon: <CheckCircle2 className="h-4 w-4" />, variant: "default", showBadge: false },
  FAILED: { label: "Failed", icon: <XCircle className="h-4 w-4" />, variant: "destructive", showBadge: true },
  SKIPPED: { label: "Skipped", icon: <Clock className="h-4 w-4" />, variant: "outline", showBadge: false },
  ALREADY_DONE: { label: "Already Done", icon: <CheckCircle2 className="h-4 w-4" />, variant: "outline", showBadge: false },
};

export function Phase4PublishSteps({
  batchRunId,
  steps,
  onStepExecuted,
  onNavigateToDeduped,
  sourcesEnabled = [],
  onRunStep,
}: Phase4PublishStepsProps) {
  const { siteState, promoting, publishing, promoteToAdmin, publishToLive, refetch } = useSiteState();
  const [acceptedCount, setAcceptedCount] = useState<number>(0);
  const [rejectedCount, setRejectedCount] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [publishLiveDialogOpen, setPublishLiveDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());
  const [lastPromoteResult, setLastPromoteResult] = useState<PromoteResult | null>(null);
  const [lastPublishResult, setLastPublishResult] = useState<PublishLiveResult | null>(null);
  // CR319: S6.5 state
  const [s65Running, setS65Running] = useState(false);
  const [s65Summary, setS65Summary] = useState<{
    total_brands_detected: number;
    matched_existing: number;
    created_pending: number;
    deals_scanned: number;
  } | null>(null);

  const s5Step = steps.find(s => s.step_key === "S5");
  const s6Step = steps.find(s => s.step_key === "S6");
  const s65Step = steps.find(s => s.step_key === "S6_5");
  const s7Step = steps.find(s => s.step_key === "S7");
  const s8Step = steps.find(s => s.step_key === "S8");
  const [cleanupConfig, setCleanupConfig] = useState<CleanupConfig>(DEFAULT_CLEANUP_CONFIG);
  const [cleanupConfigSaving, setCleanupConfigSaving] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupPreviewRunning, setCleanupPreviewRunning] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState<CleanupPreviewSummary | null>(null);
  const [cleanupTraceId, setCleanupTraceId] = useState<string | null>(null);

  // CR319: Fetch S6.5 summary from step config_json
  useEffect(() => {
    if (s65Step?.status === "COMPLETE" || s65Step?.status === "SKIPPED") {
      const cfg = s65Step.config_json as Record<string, unknown> | null;
      const summary = cfg?.summary as typeof s65Summary;
      if (summary) setS65Summary(summary);
    }
  }, [s65Step?.status, s65Step?.config_json]);

  // Fetch accepted/rejected counts from deduped table
  useEffect(() => {
    const fetchCounts = async () => {
      const { data: deduped, error } = await supabase
        .from("cr075_staging_deals_deduped")
        .select("review_status, publish_status")
        .eq("batch_run_id", batchRunId);

      if (!error && deduped) {
        const accepted = deduped.filter(d => d.review_status === "ACCEPTED").length;
        const rejected = deduped.filter(d => d.review_status === "REJECTED").length;
        const pending = deduped.length - accepted - rejected;
        setAcceptedCount(accepted);
        setRejectedCount(rejected);
        setPendingCount(Math.max(0, pending));
      }
    };

    fetchCounts();
  }, [batchRunId, s7Step?.status]);

  useEffect(() => {
    const fetchCleanupConfig = async () => {
      const { data, error } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();

      if (error) {
        console.error("[S8] Failed to fetch cleanup config:", error);
        return;
      }

      const runConfig = (data?.config_json ?? {}) as Record<string, unknown>;
      const stored = (runConfig.cleanupConfig ?? {}) as Partial<CleanupConfig>;
      setCleanupConfig({
        s8_1_storageEnabled: stored.s8_1_storageEnabled ?? DEFAULT_CLEANUP_CONFIG.s8_1_storageEnabled,
        s8_2_dbHeavyColumnsEnabled: stored.s8_2_dbHeavyColumnsEnabled ?? DEFAULT_CLEANUP_CONFIG.s8_2_dbHeavyColumnsEnabled,
        s8_3_stagingPruneEnabled: stored.s8_3_stagingPruneEnabled ?? DEFAULT_CLEANUP_CONFIG.s8_3_stagingPruneEnabled,
        s8_mode: stored.s8_mode ?? DEFAULT_CLEANUP_CONFIG.s8_mode,
        s8_cleanup_preserve_candidate_assets_enabled:
          stored.s8_cleanup_preserve_candidate_assets_enabled
            ?? DEFAULT_CLEANUP_CONFIG.s8_cleanup_preserve_candidate_assets_enabled,
        s8_capture_publish_snapshot_enabled:
          stored.s8_capture_publish_snapshot_enabled
            ?? DEFAULT_CLEANUP_CONFIG.s8_capture_publish_snapshot_enabled,
      });
    };
    fetchCleanupConfig();
  }, [batchRunId]);

  const toggleStep = (stepKey: string) => {
    const newOpen = new Set(openSteps);
    if (newOpen.has(stepKey)) {
      newOpen.delete(stepKey);
    } else {
      newOpen.add(stepKey);
    }
    setOpenSteps(newOpen);
  };

  // Derive S6 status from backend state
  const getS6Status = (): { status: string; message: string } => {
    if (!s5Step || s5Step.status !== "COMPLETE") {
      return { status: "WAITING", message: "Waiting for S5 (Dedup) to complete" };
    }
    if ((s5Step.records_out ?? 0) === 0) {
      return { status: "SKIPPED", message: "No deduped deals to review" };
    }
    if (s7Step?.status === "COMPLETE" || siteState?.admin_batch_run_id === batchRunId) {
      return { status: "COMPLETE", message: `Reviewed ${acceptedCount} accepted, ${rejectedCount} rejected` };
    }
    if (s6Step?.status === "COMPLETE") {
      return { status: "COMPLETE", message: `Review complete: ${acceptedCount} accepted, ${rejectedCount} rejected` };
    }
    // If there are no pending review decisions left, S6 is effectively complete.
    if (pendingCount === 0 && (acceptedCount + rejectedCount) > 0) {
      return { status: "COMPLETE", message: `Review complete: ${acceptedCount} accepted, ${rejectedCount} rejected` };
    }
    return { status: "READY", message: `${pendingCount} deals ready for review` };
  };

  // CR319: Derive S6.5 status
  const getS65Status = (): { status: string; message: string; canRun: boolean } => {
    const s6Status = getS6Status();
    if (s6Status.status !== "COMPLETE") {
      return { status: "WAITING", message: "Waiting for S6 (Review) to complete", canRun: false };
    }
    if (s65Running) {
      return { status: "RUNNING", message: "Detecting reward brands...", canRun: false };
    }
    if (s65Step?.status === "COMPLETE") {
      const msg = s65Summary
        ? `Detected ${s65Summary.total_brands_detected} brands in ${s65Summary.deals_scanned} deals; created ${s65Summary.created_pending} new pending merchants.`
        : "Complete";
      return { status: "COMPLETE", message: msg, canRun: true };
    }
    if (s65Step?.status === "SKIPPED") {
      return { status: "SKIPPED", message: s65Step.skipped_reason || "No reward brands found", canRun: true };
    }
    if (s65Step?.status === "FAILED") {
      return { status: "FAILED", message: s65Step.error_message || "Detection failed", canRun: true };
    }
    // Flyer runs can use candidate matching even with 0 accepted deals.
    const isFlyerRun = Array.isArray(sourcesEnabled) && sourcesEnabled.includes("FLYER");
    if (!isFlyerRun && acceptedCount === 0) {
      return { status: "WAITING", message: "No accepted deals to scan", canRun: false };
    }
    if (isFlyerRun && acceptedCount === 0) {
      return { status: "READY", message: "Flyer run ready: candidate-based matching available", canRun: true };
    }
    return { status: "READY", message: `${acceptedCount} accepted deals ready for brand detection`, canRun: true };
  };

  // CR319: Run S6.5
  const handleRunS65 = async () => {
    setS65Running(true);
    try {
      const response = await supabase.functions.invoke("batch-run-reward-brand-detect", {
        body: { batch_run_id: batchRunId, step_id: s65Step?.id },
      });
      if (response.error) throw response.error;
      const result = response.data;
      if (result) {
        setS65Summary({
          total_brands_detected: result.total_brands_detected || 0,
          matched_existing: result.matched_existing || 0,
          created_pending: result.created_pending || 0,
          deals_scanned: result.deals_scanned || 0,
        });
        toast.success(`Detected ${result.total_brands_detected || 0} reward brands, created ${result.created_pending || 0} pending merchants`);
      }
      onStepExecuted();
    } catch (err) {
      console.error("[S6.5] Error:", err);
      toast.error("Failed to run reward brand detection");
    } finally {
      setS65Running(false);
    }
  };

  // Derive S7A (Promote to Admin) status
  const getS7AStatus = (): { status: string; message: string; canPromote: boolean } => {
    const s6Status = getS6Status();
    if (s6Status.status === "WAITING") {
      return { status: "WAITING", message: "Waiting for review step", canPromote: false };
    }

    // Check if this batch is already promoted to Admin
    if (siteState?.admin_batch_run_id === batchRunId) {
      return {
        status: "COMPLETE",
        message: `Promoted to Admin: ${siteState.admin_deal_count ?? 0} deals`,
        canPromote: false
      };
    }

    if (promoting) {
      return { status: "RUNNING", message: "Promoting to Admin...", canPromote: false };
    }

    if (lastPromoteResult?.success === false) {
      return {
        status: "FAILED",
        message: lastPromoteResult.message || lastPromoteResult.error || "Promotion failed",
        canPromote: true
      };
    }

    if (acceptedCount === 0) {
      return { status: "WAITING", message: "No accepted deals to promote", canPromote: false };
    }

    return { status: "READY", message: `${acceptedCount} deals ready for Admin`, canPromote: true };
  };

  // Derive S7B (Publish Admin to Live) status
  const getS7BStatus = (): { status: string; message: string; canPublish: boolean } => {
    // Check if Admin snapshot exists
    if (!siteState?.admin_snapshot_id) {
      return { status: "WAITING", message: "Waiting for Admin promotion", canPublish: false };
    }

    // Check if Admin snapshot is already live
    if (siteState.admin_snapshot_id === siteState.active_snapshot_id) {
      return {
        status: "ALREADY_DONE",
        message: `Already live: ${siteState.published_deal_count ?? 0} deals`,
        canPublish: false
      };
    }

    if (publishing) {
      return { status: "RUNNING", message: "Publishing to Live...", canPublish: false };
    }

    if (lastPublishResult?.success === false) {
      return {
        status: "FAILED",
        message: lastPublishResult.message || lastPublishResult.error || "Publish failed",
        canPublish: true
      };
    }

    return {
      status: "READY",
      message: `Admin snapshot ready (${siteState.admin_deal_count ?? 0} deals)`,
      canPublish: true
    };
  };

  // Check statuses
  const isThisBatchAdminActive = siteState?.admin_batch_run_id === batchRunId;
  const isThisBatchLiveActive = siteState?.active_batch_run_id === batchRunId;

  const handlePromoteToAdmin = async () => {
    if (confirmText !== "PROMOTE") {
      toast.error("Please type PROMOTE to confirm");
      return;
    }

    const result = await promoteToAdmin(batchRunId, { notes: `Promoted from Batch Run Steps UI` });
    setLastPromoteResult(result);

    if (result.success) {
      setPromoteDialogOpen(false);
      setConfirmText("");
      onStepExecuted();
      refetch();
    }
  };

  const handlePublishToLive = async () => {
    if (confirmText !== "PUBLISH") {
      toast.error("Please type PUBLISH to confirm");
      return;
    }

    const result = await publishToLive({ notes: `Published from Batch Run Steps UI` });
    setLastPublishResult(result);

    if (result.success) {
      setPublishLiveDialogOpen(false);
      setConfirmText("");
      onStepExecuted();
      refetch();
    }
  };

  const updateCleanupConfig = async (next: CleanupConfig) => {
    setCleanupConfig(next);
    setCleanupConfigSaving(true);
    try {
      const { data, error } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();
      if (error) throw error;

      const currentConfig = (data?.config_json ?? {}) as Record<string, unknown>;
      const mergedConfig = {
        ...currentConfig,
        cleanupConfig: next,
      };

      const { error: updateError } = await supabase
        .from("cr075_batch_runs")
        .update({ config_json: mergedConfig as any })
        .eq("id", batchRunId);
      if (updateError) throw updateError;
    } catch (err) {
      console.error("[S8] Failed to persist cleanup config:", err);
      toast.error("Failed to save S8 cleanup toggles");
    } finally {
      setCleanupConfigSaving(false);
    }
  };

  const handleRunCleanup = async () => {
    if (cleanupConfig.s8_mode === "PURGE_ALL") {
      const ack = window.prompt('Dangerous action. Type "PURGE_ALL" to confirm irreversible S8 storage deletion:');
      if (ack !== "PURGE_ALL") {
        toast.error("S8 cleanup cancelled");
        return;
      }
    }

    setCleanupRunning(true);
    try {
      const response = await supabase.functions.invoke("batch-run-cleanup-s8", {
        body: {
          batch_run_id: batchRunId,
          cleanup_config: cleanupConfig,
        },
      });
      if (response.error) throw response.error;
      setCleanupTraceId(response.data?.trace_id ?? null);
      if (!response.data?.ok) {
        throw new Error(response.data?.message || "S8 cleanup failed");
      }
      toast.success("S8 cleanup completed");
      onStepExecuted();
    } catch (err) {
      console.error("[S8] Cleanup failed:", err);
      toast.error("Failed to run S8 cleanup");
    } finally {
      setCleanupRunning(false);
    }
  };

  const handlePreviewCleanup = async () => {
    setCleanupPreviewRunning(true);
    try {
      const response = await supabase.functions.invoke("batch-run-cleanup-s8", {
        body: {
          batch_run_id: batchRunId,
          cleanup_config: cleanupConfig,
          dry_run: true,
        },
      });
      if (response.error) throw response.error;
      if (!response.data?.ok) throw new Error(response.data?.message || "Preview failed");
      setCleanupPreview((response.data?.summary ?? null) as CleanupPreviewSummary | null);
      setCleanupTraceId(response.data?.trace_id ?? null);
      toast.success("S8 dry-run preview refreshed");
    } catch (err) {
      console.error("[S8] Preview failed:", err);
      toast.error("Failed to preview S8 cleanup");
    } finally {
      setCleanupPreviewRunning(false);
    }
  };

  const s6StatusInfo = getS6Status();
  // CR319: S6.5 Reward Brand detection status
  const s65StatusInfo = getS65Status();
  const s7AStatusInfo = getS7AStatus();
  const s7BStatusInfo = getS7BStatus();
  const s8StatusInfo = (() => {
    if (cleanupRunning || s8Step?.status === "RUNNING") {
      return { status: "RUNNING", message: "Running cleanup..." };
    }
    if (s8Step?.status === "COMPLETE" || s8Step?.status === "COMPLETE_WITH_ERRORS") {
      return { status: "COMPLETE", message: s8Step.records_out > 0 ? `${s8Step.records_out} cleanup actions applied` : "Cleanup complete" };
    }
    if (s8Step?.status === "FAILED") {
      return { status: "FAILED", message: s8Step.error_message || "Cleanup failed" };
    }
    if (isThisBatchLiveActive) {
      return { status: "READY", message: "Optional cleanup available" };
    }
    return { status: "WAITING", message: "Toggles can be set now. Run after publish to Live." };
  })();

  const renderStepRow = (
    stepKey: string,
    stepName: string,
    icon: React.ReactNode,
    statusInfo: { status: string; message: string },
    actions?: React.ReactNode,
    expandedContent?: React.ReactNode
  ) => {
    const config = stepStatusConfig[statusInfo.status] || stepStatusConfig.NOT_STARTED;
    const isOpen = openSteps.has(stepKey);

    return (
      <Collapsible key={stepKey} open={isOpen} onOpenChange={() => toggleStep(stepKey)}>
        <div className="border rounded-lg">
          <CollapsibleTrigger asChild>
            <button className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className={statusInfo.status === "COMPLETE" ? "text-primary" :
                  statusInfo.status === "ALREADY_DONE" ? "text-primary" :
                    statusInfo.status === "FAILED" ? "text-destructive" :
                      statusInfo.status === "RUNNING" ? "text-accent-foreground" :
                        "text-muted-foreground"}>
                  {statusInfo.status === "RUNNING" ? <Loader2 className="h-4 w-4 animate-spin" /> : config.icon}
                </div>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {icon}
                    {stepKey}: {stepName}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {statusInfo.message}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {actions}
                {config.showBadge && (
                  <Badge variant={config.variant} className="text-xs">
                    {config.label}
                  </Badge>
                )}
                {expandedContent && <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
              </div>
            </button>
          </CollapsibleTrigger>
          {expandedContent && (
            <CollapsibleContent>
              <div className="px-4 pb-4 pt-2 border-t space-y-3">
                {expandedContent}
              </div>
            </CollapsibleContent>
          )}
        </div>
      </Collapsible>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Publish Steps
        </Label>
        <div className="flex items-center gap-2">
          {isThisBatchAdminActive && !isThisBatchLiveActive && (
            <Badge variant="secondary" className="gap-1">
              <ArrowRight className="h-3 w-3" />
              Admin Active
            </Badge>
          )}
          {isThisBatchLiveActive && (
            <Badge variant="default" className="gap-1">
              <Globe className="h-3 w-3" />
              Live on Site
            </Badge>
          )}
        </div>
      </div>

      {/* Site State Warning */}
      {!siteState?.active_snapshot_id && (
        <Alert variant="default" className="border-muted bg-muted/50">
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
          <AlertDescription className="text-muted-foreground">
            No active snapshot is currently set. The site may be using a default or legacy source.
          </AlertDescription>
        </Alert>
      )}

      {/* S6: Review */}
      {renderStepRow(
        "S6",
        "Review Deduped Deals",
        <Eye className="h-4 w-4" />,
        s6StatusInfo,
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-xs h-7"
          onClick={() => onNavigateToDeduped()}
        >
          <ExternalLink className="h-3 w-3" />
          Go to Deduped
        </Button>,
        s6StatusInfo.status !== "WAITING" && (
          <div className="text-sm space-y-2">
            {/* CR551: S1.5 snippet warning removed — S1.4 handles snippets now */}
            <div className="flex items-center gap-4">
              <span><strong>{acceptedCount}</strong> accepted</span>
              <span><strong>{rejectedCount}</strong> rejected</span>
              <span className="text-muted-foreground">({acceptedCount + rejectedCount} total reviewed)</span>
            </div>
            <p className="text-muted-foreground text-xs">
              Review happens in the Deduped Deals tab. Accept or reject deals before publishing.
            </p>
          </div>
        )
      )}

      {/* CR319: S6.5: Detect Reward Brand Merchants */}
      {renderStepRow(
        "S6.5",
        "Detect Reward Brand Merchants",
        <Users className="h-4 w-4" />,
        s65StatusInfo,
        s65StatusInfo.canRun && (
          <Button
            size="sm"
            variant={s65Step?.status === "COMPLETE" ? "outline" : "default"}
            className="gap-1 h-7"
            onClick={handleRunS65}
            disabled={s65Running}
          >
            {s65Running ? <Loader2 className="h-3 w-3 animate-spin" /> : <SearchIcon className="h-3 w-3" />}
            {s65Step?.status === "COMPLETE" || s65Step?.status === "SKIPPED" ? "Re-run" : "Run"}
          </Button>
        ),
        (s65StatusInfo.status === "COMPLETE" || s65StatusInfo.status === "SKIPPED") && (
          <div className="text-sm space-y-3">
            {s65Summary && (
              <div className="space-y-2">
                <div className="flex items-center gap-4 flex-wrap">
                  <span><strong>{s65Summary.total_brands_detected}</strong> brands detected</span>
                  <span><strong>{s65Summary.matched_existing}</strong> matched existing</span>
                  <span><strong>{s65Summary.created_pending}</strong> new pending merchants</span>
                  <span className="text-muted-foreground">({s65Summary.deals_scanned} deals scanned)</span>
                </div>
                {s65Summary.created_pending > 0 && (
                  <p className="text-muted-foreground text-xs">
                    New merchants added to Merchant Directory → Pending tab for review.
                  </p>
                )}
              </div>
            )}
            {/* CR331: Reward merchants diagnostics table */}
            <S65RewardMerchantsTable batchRunId={batchRunId} />
          </div>
        )
      )}

      {/* S7A: Promote to Admin */}
      {renderStepRow(
        "S7A",
        "Promote to Admin",
        <ArrowRight className="h-4 w-4" />,
        s7AStatusInfo,
        s7AStatusInfo.canPromote && (
          <Button
            size="sm"
            variant="secondary"
            className="gap-1 h-7"
            onClick={() => setPromoteDialogOpen(true)}
            disabled={promoting}
          >
            {promoting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}
            Promote {acceptedCount} Deals
          </Button>
        ),
        (s7AStatusInfo.status === "COMPLETE" || s7AStatusInfo.status === "FAILED") && (
          <div className="text-sm space-y-4">
            {isThisBatchAdminActive && siteState && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><strong>Snapshot:</strong> {siteState.admin_snapshot_id}</div>
                <div><strong>Deal Count:</strong> {siteState.admin_deal_count}</div>
                {siteState.admin_promoted_at && (
                  <div className="col-span-2"><strong>Promoted At:</strong> {new Date(siteState.admin_promoted_at).toLocaleString()}</div>
                )}
              </div>
            )}
            {lastPromoteResult?.success === false && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{lastPromoteResult.message || lastPromoteResult.error}</AlertDescription>
              </Alert>
            )}
            {isThisBatchAdminActive && !isThisBatchLiveActive && (
              <Alert variant="default" className="border-warning/50 bg-warning/10">
                <AlertCircle className="h-4 w-4 text-warning" />
                <AlertDescription className="text-warning-foreground text-xs">
                  <strong>Not visible on Deal Finder.</strong> Click "Publish to Live" (S7B) to make these deals public.
                </AlertDescription>
              </Alert>
            )}
            {isThisBatchAdminActive && isThisBatchLiveActive && (
              <p className="text-primary text-xs flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                This batch is the current Admin snapshot and is live on the public site.
              </p>
            )}

            {/* CR283: Snapshot Deal Decisions Panel */}
            {isThisBatchAdminActive && siteState?.admin_snapshot_id && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Deal Visibility Decisions</Label>
                </div>
                <SnapshotDealDecisionsPanel
                  snapshotId={siteState.admin_snapshot_id}
                  compact={true}
                  maxHeight="300px"
                />
              </div>
            )}
          </div>
        )
      )}

      {/* S7B: Publish Admin to Live */}
      {renderStepRow(
        "S7B",
        "Publish Admin to Live",
        <Globe className="h-4 w-4" />,
        s7BStatusInfo,
        s7BStatusInfo.canPublish && (
          <Button
            size="sm"
            className="gap-1 h-7"
            onClick={() => setPublishLiveDialogOpen(true)}
            disabled={publishing}
          >
            {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Publish to Live
          </Button>
        ),
        (s7BStatusInfo.status === "ALREADY_DONE" || s7BStatusInfo.status === "FAILED") && (
          <div className="text-sm space-y-2">
            {isThisBatchLiveActive && siteState && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><strong>Live Snapshot:</strong> {siteState.active_snapshot_id}</div>
                <div><strong>Deal Count:</strong> {siteState.published_deal_count}</div>
                {siteState.published_at && (
                  <div className="col-span-2"><strong>Published At:</strong> {new Date(siteState.published_at).toLocaleString()}</div>
                )}
              </div>
            )}
            {lastPublishResult?.success === false && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{lastPublishResult.message || lastPublishResult.error}</AlertDescription>
              </Alert>
            )}
            {isThisBatchLiveActive && (
              <p className="text-primary text-xs flex items-center gap-1">
                <Globe className="h-3 w-3" />
                This batch is currently visible on the public site.
              </p>
            )}
          </div>
        )
      )}

      {/* S8 moved to CLEANUP group — CR553 */}
      <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide pt-4 block">
        Cleanup
      </Label>

      {/* S8: Cleanup (Optional) */}
      {renderStepRow(
        "S8",
        "Lifecycle Cleanup",
        <Trash2 className="h-4 w-4" />,
        s8StatusInfo,
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1 h-7"
            onClick={handlePreviewCleanup}
            disabled={cleanupPreviewRunning || cleanupRunning || cleanupConfigSaving || !isThisBatchLiveActive}
            title={!isThisBatchLiveActive ? "Publish to Live first, then preview cleanup." : undefined}
          >
            {cleanupPreviewRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Dry Run
          </Button>
          <Button
            size="sm"
            variant={isThisBatchLiveActive ? "default" : "outline"}
            className="gap-1 h-7"
            onClick={handleRunCleanup}
            disabled={cleanupRunning || cleanupPreviewRunning || cleanupConfigSaving || !isThisBatchLiveActive}
            title={!isThisBatchLiveActive ? "Publish to Live first, then run cleanup." : undefined}
          >
            {cleanupRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            Run Cleanup
          </Button>
        </div>,
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select lifecycle objects to prune for this batch run.
          </p>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">S8 Mode</Label>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={cleanupConfig.s8_mode}
              disabled={cleanupConfigSaving}
              onChange={(e) =>
                updateCleanupConfig({
                  ...cleanupConfig,
                  s8_mode: e.target.value as CleanupConfig["s8_mode"],
                })
              }
            >
              <option value="KEEP_CONFIRMED_ONLY">KEEP_CONFIRMED_ONLY (Recommended)</option>
              <option value="KEEP_ALL">KEEP_ALL</option>
              <option value="PURGE_ALL">PURGE_ALL (Danger)</option>
            </select>
            <p className="text-xs text-muted-foreground">
              KEEP_CONFIRMED_ONLY preserves manifest-linked candidate page and snippet assets.
            </p>
          </div>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">S8.P: Preserve Candidate Assets</Label>
                <p className="text-xs text-muted-foreground">
                  Use manifest preserve pointers as deletion guardrails in KEEP_CONFIRMED_ONLY mode.
                </p>
              </div>
              <Switch
                checked={cleanupConfig.s8_cleanup_preserve_candidate_assets_enabled}
                disabled={cleanupConfigSaving}
                onCheckedChange={(checked) =>
                  updateCleanupConfig({ ...cleanupConfig, s8_cleanup_preserve_candidate_assets_enabled: checked })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">S8.S: Capture Publish Snapshot</Label>
                <p className="text-xs text-muted-foreground">
                  Ensure immutable run + merchant analytics snapshot exists before destructive cleanup.
                </p>
              </div>
              <Switch
                checked={cleanupConfig.s8_capture_publish_snapshot_enabled}
                disabled={cleanupConfigSaving}
                onCheckedChange={(checked) =>
                  updateCleanupConfig({ ...cleanupConfig, s8_capture_publish_snapshot_enabled: checked })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">S8.1: Storage Image Clean</Label>
                <p className="text-xs text-muted-foreground">
                  Removes rendered full-page images, OCR-safe variants, and original PDFs.
                </p>
              </div>
              <Switch
                checked={cleanupConfig.s8_1_storageEnabled}
                disabled={cleanupConfigSaving}
                onCheckedChange={(checked) =>
                  updateCleanupConfig({ ...cleanupConfig, s8_1_storageEnabled: checked })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">S8.2: Heavy Columns Clean (OCR)</Label>
                <p className="text-xs text-muted-foreground">
                  Nullifies heavy OCR text blobs to reclaim database memory.
                </p>
              </div>
              <Switch
                checked={cleanupConfig.s8_2_dbHeavyColumnsEnabled}
                disabled={cleanupConfigSaving}
                onCheckedChange={(checked) =>
                  updateCleanupConfig({ ...cleanupConfig, s8_2_dbHeavyColumnsEnabled: checked })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">S8.3: Staging & State Prune</Label>
                <p className="text-xs text-muted-foreground">
                  Deletes processed staging and lifecycle rows for this run.
                </p>
              </div>
              <Switch
                checked={cleanupConfig.s8_3_stagingPruneEnabled}
                disabled={cleanupConfigSaving}
                onCheckedChange={(checked) =>
                  updateCleanupConfig({ ...cleanupConfig, s8_3_stagingPruneEnabled: checked })
                }
              />
            </div>
          </div>

          {cleanupPreview && (
            <div className="rounded-md border border-border/70 p-3 space-y-2">
              <div className="text-sm font-medium">Dry-Run Summary</div>
              <div className="text-xs text-muted-foreground grid grid-cols-2 gap-2">
                <div><strong>Mode:</strong> {cleanupPreview.mode}</div>
                <div><strong>Manifest rows:</strong> {cleanupPreview.preserve.manifest_rows}</div>
                <div><strong>Retained candidate pages:</strong> {cleanupPreview.preserve.candidate_pages}</div>
                <div><strong>Retained snippets:</strong> {cleanupPreview.preserve.snippets}</div>
                <div><strong>Assets considered:</strong> {cleanupPreview.storage.considered}</div>
                <div><strong>Assets to delete:</strong> {cleanupPreview.storage.to_delete}</div>
                <div><strong>Skipped by preserve set:</strong> {cleanupPreview.storage.skipped_preserve}</div>
                {cleanupTraceId && <div><strong>Trace:</strong> {cleanupTraceId}</div>}
              </div>

              {cleanupPreview.bucket_stats.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <strong>Per bucket:</strong>{" "}
                  {cleanupPreview.bucket_stats
                    .map((b) => `${b.bucket} planned=${b.planned} deleted=${b.deleted} failed=${b.failed}`)
                    .join(" · ")}
                </div>
              )}

              {(cleanupPreview.warnings?.length ?? 0) > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{cleanupPreview.warnings?.join(" | ")}</AlertDescription>
                </Alert>
              )}

              {(cleanupPreview.errors?.length ?? 0) > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{cleanupPreview.errors?.join(" | ")}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      )}

      {/* Promote to Admin Confirmation Dialog */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote Deals to Admin</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>You are about to promote <strong>{acceptedCount}</strong> accepted deals to the Admin snapshot.</p>
              <p><strong>{rejectedCount}</strong> rejected deals will be excluded.</p>
              <p className="text-sm text-muted-foreground mt-4">
                This will upsert deals into production by dedupe_key, create an Admin snapshot, and append to archive.
                <strong className="block mt-1">The deals will NOT be visible to the public until you Publish to Live.</strong>
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-promote">Type <strong>PROMOTE</strong> to confirm</Label>
              <Input
                id="confirm-promote"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PROMOTE"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPromoteDialogOpen(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handlePromoteToAdmin}
              disabled={confirmText !== "PROMOTE" || promoting}
            >
              {promoting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Promoting...
                </>
              ) : (
                "Promote to Admin"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish to Live Confirmation Dialog */}
      <Dialog open={publishLiveDialogOpen} onOpenChange={setPublishLiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Admin Snapshot to Live</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>You are about to make the Admin snapshot visible to the public.</p>
              <div className="bg-muted rounded-md p-3 mt-2 text-xs space-y-1">
                <p><strong>Admin Snapshot:</strong> {siteState?.admin_snapshot_id}</p>
                <p><strong>Deal Count:</strong> {siteState?.admin_deal_count}</p>
                {siteState?.active_snapshot_id && (
                  <p><strong>Current Live:</strong> {siteState.active_snapshot_id} ({siteState.published_deal_count} deals)</p>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                This will update the public site immediately. The previous live snapshot will be recorded for rollback.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-publish">Type <strong>PUBLISH</strong> to confirm</Label>
              <Input
                id="confirm-publish"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PUBLISH"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPublishLiveDialogOpen(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handlePublishToLive}
              disabled={confirmText !== "PUBLISH" || publishing}
            >
              {publishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                "Publish to Live"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
